import { useState, useEffect } from 'react';
import { attachmentsAPI } from '../services/api';
import './AttachmentSection.css';

/**
 * Format bytes to human-readable string.
 */
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Map MIME type to a short label.
 */
function getFileTypeLabel(mimeType) {
  if (!mimeType) return 'File';
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('image')) return 'Image';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('.sheet')) return 'Excel';
  if (mimeType.includes('word') || mimeType.includes('.document')) return 'Word';
  if (mimeType.includes('text')) return 'Text';
  return 'File';
}

/**
 * AttachmentSection - Displays attachments for a shipment with download/delete capabilities.
 *
 * Props:
 *   shipmentId - ID of the shipment
 *   currentUser - { id, role, userId }
 *   shipmentOwnerId - user_id of the shipment owner
 */
function AttachmentSection({ shipmentId, currentUser, shipmentOwnerId }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  useEffect(() => {
    fetchAttachments();
  }, [shipmentId]);

  const fetchAttachments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await attachmentsAPI.getForShipment(shipmentId);
      setAttachments(response.data.attachments || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view attachments for this shipment.');
      } else {
        setError('Failed to load attachments.');
      }
      console.error('Attachment fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attachmentId, filename) => {
    if (!window.confirm(`Delete attachment "${filename}"? This cannot be undone.`)) return;
    setDeleteError('');
    try {
      await attachmentsAPI.delete(attachmentId);
      fetchAttachments(); // Refresh list
    } catch (err) {
      setDeleteError(`Failed to delete "${filename}".`);
      console.error('Attachment delete error:', err);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="attachment-section">
      <div className="section-header">ATTACHMENTS</div>
      <div className="detail-section">
        {loading && <p className="attachment-loading">Loading attachments...</p>}

        {error && <p className="attachment-error">{error}</p>}

        {deleteError && <p className="attachment-error">{deleteError}</p>}

        {!loading && !error && attachments.length === 0 && (
          <p className="attachment-empty">No attachments for this shipment.</p>
        )}

        {!loading && !error && attachments.length > 0 && (
          <div className="attachment-list">
            {attachments.map((att) => (
              <div key={att.id} className="attachment-row">
                <div className="attachment-info">
                  <span className={`attachment-type-badge ${getFileTypeLabel(att.mime_type).toLowerCase()}`}>
                    {getFileTypeLabel(att.mime_type)}
                  </span>
                  <a
                    href={attachmentsAPI.getDownloadUrl(att.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-filename"
                  >
                    {att.filename}
                  </a>
                  <span className="attachment-size">{formatFileSize(att.file_size)}</span>
                  <span className="attachment-date">{formatDate(att.uploaded_at)}</span>
                  {isAdmin && att.uploaded_by && (
                    <span className="attachment-uploader">by {att.uploaded_by}</span>
                  )}
                </div>
                <div className="attachment-actions">
                  <a
                    href={attachmentsAPI.getDownloadUrl(att.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-download"
                  >
                    Download
                  </a>
                  {isAdmin && (
                    <button
                      className="btn-delete-attachment"
                      onClick={() => handleDelete(att.id, att.filename)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AttachmentSection;
